import { useCallback, useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nProvider";
import { WORKERS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Upload, Download, Trash2, Search, FolderArchive } from "lucide-react";
import { Link } from "react-router-dom";

export default function WorkersPage() {
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState("all");
  const [fSearch, setFSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", first_name: "", last_name: "", tax_code: "", role: "", department: "", email: "" });
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const q = filterCompany !== "all" ? `?company_id=${filterCompany}` : "";
    const r = await api.get(`/workers${q}`);
    setList(r.data);
  }, [filterCompany]);
  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/workers", form);
      setOpen(false);
      setForm({ company_id: "", first_name: "", last_name: "", tax_code: "", role: "", department: "", email: "" });
      toast.success(t("save"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const importExcel = async (file) => {
    if (!filterCompany || filterCompany === "all") {
      toast.error("Seleziona un'azienda / Select a company first");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/workers/import?company_id=${filterCompany}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${t("imported")}: ${r.data.imported}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("delete") + "?")) return;
    try {
      await api.delete(`/workers/${id}`);
      toast.success(t("delete"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const downloadTemplate = async () => {
    const r = await api.get("/workers/export/template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "workers_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid={WORKERS.root} className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("workers")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {(() => {
              const _q = fSearch.trim().toLowerCase();
              const shown = _q
                ? list.filter((w) => [w.first_name, w.last_name, w.tax_code, w.role, w.department, w.email].filter(Boolean).join(" ").toLowerCase().includes(_q)).length
                : list.length;
              return _q ? `${shown} / ${list.length} ${t("workers").toLowerCase()}` : `${list.length} ${t("workers").toLowerCase()}`;
            })()}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={fSearch}
              onChange={(e) => setFSearch(e.target.value)}
              placeholder={lang === "it" ? "Cerca per nome, CF, ruolo…" : "Search by name, tax code, role…"}
              className="pl-9 w-64 h-10"
              data-testid="workers-search-input"
            />
            {fSearch && (
              <button
                type="button"
                onClick={() => setFSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
                data-testid="workers-search-clear"
                aria-label="Clear"
              >×</button>
            )}
          </div>
          <Select value={filterCompany} onValueChange={setFilterCompany}>
            <SelectTrigger className="w-56 h-10"><SelectValue placeholder={t("company")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={downloadTemplate} data-testid={WORKERS.templateBtn} className="rounded-full btn-lift">
            <Download className="w-4 h-4 mr-1.5" /> {t("download_template")}
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid={WORKERS.importBtn} className="rounded-full btn-lift">
            <Upload className="w-4 h-4 mr-1.5" /> {t("import_excel")}
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={(e) => e.target.files?.[0] && importExcel(e.target.files[0])} />

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid={WORKERS.createBtn} className="rounded-full btn-lift">
                <Plus className="w-4 h-4 mr-1.5" /> {t("add_worker")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{t("add_worker")}</DialogTitle></DialogHeader>
              <form onSubmit={submit} data-testid={WORKERS.form} className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>{t("company")}</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger><SelectValue placeholder={t("company")} /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("first_name")}</Label><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid={WORKERS.firstName} /></div>
                <div><Label>{t("last_name")}</Label><Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid={WORKERS.lastName} /></div>
                <div><Label>{t("tax_code")}</Label><Input value={form.tax_code} onChange={(e) => setForm({ ...form, tax_code: e.target.value })} data-testid={WORKERS.tax} /></div>
                <div><Label>{t("role")}</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid={WORKERS.role} /></div>
                <div><Label>{t("department")}</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
                <div><Label>{t("email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <DialogFooter className="col-span-2">
                  <Button type="submit" data-testid={WORKERS.submit} className="rounded-full btn-lift">{t("save")}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-5 py-3">{t("last_name")}</th>
              <th className="text-left px-5 py-3">{t("first_name")}</th>
              <th className="text-left px-5 py-3">{t("tax_code")}</th>
              <th className="text-left px-5 py-3">{t("role")}</th>
              <th className="text-left px-5 py-3">{t("department")}</th>
              <th className="text-left px-5 py-3">{t("email")}</th>
              <th className="text-right px-5 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const _q = fSearch.trim().toLowerCase();
              const filtered = _q
                ? list.filter((w) => [w.first_name, w.last_name, w.tax_code, w.role, w.department, w.email].filter(Boolean).join(" ").toLowerCase().includes(_q))
                : list;
              return (
                <>
                  {filtered.map((w) => (
                    <tr key={w.id} className="border-t border-border hover:bg-muted/40">
                      <td className="px-5 py-3 font-medium">{w.last_name}</td>
                      <td className="px-5 py-3">{w.first_name}</td>
                      <td className="px-5 py-3 text-muted-foreground">{w.tax_code || "—"}</td>
                      <td className="px-5 py-3">{w.role || "—"}</td>
                      <td className="px-5 py-3">{w.department || "—"}</td>
                      <td className="px-5 py-3 text-muted-foreground">{w.email || "—"}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center gap-1.5 justify-end">
                          <Link
                            to={`/workers/${w.id}/documents`}
                            className="text-muted-foreground hover:text-primary btn-lift"
                            title={lang === "it" ? "Vedi documenti storici" : "See historical documents"}
                            data-testid={`worker-docs-link-${w.id}`}
                          >
                            <FolderArchive className="w-4 h-4" />
                          </Link>
                          <button onClick={() => remove(w.id)} data-testid={`delete-worker-${w.id}`} className="text-muted-foreground hover:text-destructive btn-lift">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">{_q ? (lang === "it" ? "Nessun lavoratore corrisponde alla ricerca" : "No workers match the search") : t("no_data")}</td></tr>}
                </>
              );
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}
